import React, { CSSProperties } from 'react';

interface TourWrapperProps {
  tourId?: string;
  children: React.ReactNode;
  className?: string;
  style?: CSSProperties;
}

/**
 * TourWrapper - Bridge component for React Native Web tour support
 *
 * **Platform:** Web only (React Native Web)
 * **Purpose:** Ensures data-tour attributes render to DOM for driver.js
 * **Note:** This component renders a native HTML <div>, not a React Native View
 *
 * ## Problem
 * React Native Web's View and TouchableOpacity components don't properly render
 * data-* attributes to the DOM, which causes driver.js querySelector to fail.
 *
 * ## Solution
 * This wrapper ensures the data-tour attribute exists in the actual DOM for
 * proper tour element highlighting and positioning.
 *
 * ## Layout Behavior
 * Uses `display: inline-block; width: 100%; box-sizing: border-box` to:
 * - Properly contain children while maintaining layout
 * - Allow driver.js to correctly position popovers relative to wrapped elements
 * - Prevent layout shifts from padding/border
 *
 * @param tourId - The tour identifier for driver.js step configuration
 * @param children - React Native components to wrap
 * @param className - Optional CSS class for additional styling
 * @param style - Optional inline styles for additional styling
 *
 * @example
 * ```tsx
 * import { TOUR_ELEMENT_IDS } from 'src/features/tour/tourConfig';
 *
 * <TourWrapper tourId={TOUR_ELEMENT_IDS.EDIT_COMPANY_BTN}>
 *   <Button title="Edit" onPress={handleEdit} />
 * </TourWrapper>
 * ```
 */
export const TourWrapper: React.FC<TourWrapperProps> = ({ tourId, children, className, style }) => {
  // If no tourId provided, render children directly without wrapper
  if (!tourId) return <>{children}</>;

  // Combine default styles with user overrides
  const combinedStyle: CSSProperties = {
    display: 'inline-block',
    width: '100%',
    boxSizing: 'border-box', // Prevent layout shifts
    ...style,
  };

  return (
    <div
      data-tour={tourId}
      data-testid={`tour-wrapper-${tourId}`}
      className={className}
      style={combinedStyle}
      role="presentation" // Accessibility: wrapper is presentational only
    >
      {children}
    </div>
  );
};

export default TourWrapper;
